import React, { useState }   from "react"
import { FontAwesomeIcon }   from '@fortawesome/react-fontawesome'
import LineBase              from "./line_base"

import Buttons       from "./shared/buttons"
import Calculations  from "./shared/calculations"
import Option        from "./shared/option"
import HideInPdf     from "./shared/hide_in_pdf"
import Inactive      from "./line_inactive"
import { BreakdownTreeInputs } from "./shared/hidden_inputs"

import QuillInput from '@shared/quillInput'
import Combobox   from '@shared/combobox'

import { getRequest, pathname } from '@react/helpers/fetch'

import { lineNameFor, breakdownNameFor, formatNumber, roundNumber, breakdownTotals, breakdownTree, rollUpBreakdowns, newBreakdownTempId } from '../helpers'
import { COST_CATEGORIES, enabledCategoriesFor, CATEGORY_COLORS }                     from './shared/category_styles'

const SERVICE_INIT_MODE = {
  CHOOSE: "choose"
}

const MANUAL_OPTION_ID = "__manual__"

const DEFAULT_BREAKDOWNS = () => [
  { cost_category: "labor",     quantity: 1, unit: "h", unit_price: "", purchase_price: "", order: 1 },
  { cost_category: "materials", quantity: 1, unit: "",  unit_price: "", purchase_price: "", order: 2 }
]

const initOptions = (qline_service_blueprints) => [
  { id: MANUAL_OPTION_ID, name: I18n.t("quotes.new.service_source_manual") },
  ...(qline_service_blueprints || [])
]

// Fresh temp ids, so one prestation can come in twice.
const breakdownsFromBlueprint = (payload, parentTempId = null) => {
  const lines   = payload.qline_breakdowns || []
  const tempIds = {}
  lines.forEach(line => { if (line.temp_id) tempIds[line.temp_id] = newBreakdownTempId() })

  return lines.map(line => ({
    ...line,
    temp_id:        tempIds[line.temp_id] || newBreakdownTempId(),
    parent_temp_id: tempIds[line.parent_temp_id] || parentTempId,
    cost_category:  line.cost_category  || "materials",
    unit_price:     line.unit_price     || "",
    purchase_price: line.purchase_price || ""
  }))
}

const isBlankHtml = html => !String(html || "").replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim()

const parseNum = v => {
  const n = parseFloat(String(v).replace(",", "."))
  return Number.isFinite(n) ? n : 0
}

const deriveCoef = (pv, pa) => (pa > 0 ? pv / pa : 0)

const qlineColCount = (showMargin) => 6 + (showMargin ? 1 : 0)

const BREAKDOWN_LEFT_WIDTH  = "80%"
const BREAKDOWN_RIGHT_WIDTH = "20%"
const BREAKDOWN_INDENT      = 36

// A nested line steps in on its left side only, so its prices stay in the columns above.
const breakdownLeftColumn = indent => ({ width: `calc(${BREAKDOWN_LEFT_WIDTH} - ${indent}px)`, marginLeft: `${indent}px` })

const breakdownColumnKeys = (showMargin, showCostCategory) => {
  const useRevient = !!showCostCategory && !!showMargin
  const keys = ["category", "unit", "quantity", "quantity_total"]
  if (showMargin)  keys.push("purchase_price")
  if (useRevient)  keys.push("revient")
  if (showMargin)  keys.push("coefficient")
  keys.push("unit_price")
  return keys
}

const breakdownGridTemplate = keys => keys.map(() => "1fr").join(" ")

const breakdownHeaderLabel = key => ({
  category:       I18n.t("quotes.new.cost_category.label"),
  unit:           I18n.t("quotes.bigcard.unit"),
  quantity:       I18n.t("quotes.new.service_qty_unit"),
  quantity_total: I18n.t("quotes.new.service_qty_total"),
  purchase_price: I18n.t("quotes.new.service_purchase_price"),
  revient:        I18n.t("quotes.new.service_cost_price"),
  coefficient:    I18n.t("quotes.new.service_coefficient"),
  unit_price:     I18n.t("quotes.new.service_unit_price"),
}[key])

const breakdownColumnAlign = key =>
  (key === "description" || key === "category") ? "left" :
  (key === "unit") ? "center" : "right"

const BreakdownHeader = ({ showMargin, showCostCategory }) => {
  const keys = breakdownColumnKeys(showMargin, showCostCategory)
  return (
    <div className="service-breakdown-header" style={{ width: "100%", display: "flex", flexWrap: "wrap", padding: "0 0 4px" }}>
      <div style={{ width: BREAKDOWN_LEFT_WIDTH, display: "grid", gridTemplateColumns: breakdownGridTemplate(keys), columnGap: "5px" }}>
        { keys.map(key =>
          <div
            key   = {key}
            style = {{ textAlign: breakdownColumnAlign(key), fontSize: "11px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", color: "#6b7280", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}
          >
            {breakdownHeaderLabel(key)}
          </div>
        )}
      </div>
      <div style={{ width: BREAKDOWN_RIGHT_WIDTH }} />
    </div>
  )
}

const BreakdownRow = ({ breakdown, lineIndex, breakdownIndex, lineQuantity, indent = 0, currencySymbol, showMargin, showCostCategory, overheadCoefs, overheadCoef, enabledCategories, onChange, onRemove, onSelectProduct, onUnlinkProduct }) => {
  const [coefficientDraft, setCoefficientDraft] = useState(null)
  if (breakdown._destroy) return null

  const qty           = parseNum(breakdown.quantity)
  const pa            = parseNum(breakdown.purchase_price)
  const pv            = parseNum(breakdown.unit_price)
  const lineQ         = parseNum(lineQuantity)
  const category      = breakdown.cost_category || "materials"
  const useRevient    = !!showCostCategory && !!showMargin
  const coefForCat    = (overheadCoefs && overheadCoefs[category] != null)
                          ? parseNum(overheadCoefs[category])
                          : parseNum(overheadCoef) || 0
  const overheadMult  = 1 + coefForCat
  const revient       = useRevient ? pa * overheadMult : pa
  const coefBase      = useRevient ? revient : pa
  const coef          = coefBase > 0 ? (pv / coefBase) : 1
  const totalQty      = roundNumber(qty * lineQ)
  const totalPv       = roundNumber(qty * pv * lineQ)
  const isLabor       = category === "labor"
  const unit          = isLabor ? I18n.t("quotes.new.service_unit_labor") : (breakdown.unit || "")
  const categoryLabel = I18n.t(`quotes.new.cost_category.${category}`)
  const keys          = breakdownColumnKeys(showMargin, showCostCategory)

  const cell = key => {
    switch (key) {
      case "description":
        return (
          <input
            className   = "form-control"
            value       = {breakdown.description || ""}
            placeholder = {categoryLabel}
            onChange    = {e => onChange("description", e.target.value)}
            name        = {breakdownNameFor("description", lineIndex, breakdownIndex)}
          />
        )
      case "category":
        return (
          <select
            className = "form-control h-41px"
            value     = {category}
            onChange  = {e => onChange("cost_category", e.target.value)}
            name      = {breakdownNameFor("cost_category", lineIndex, breakdownIndex)}
          >
            { (enabledCategories || COST_CATEGORIES).map(c =>
              <option key={c} value={c}>{I18n.t(`quotes.new.cost_category.${c}`)}</option>
            )}
          </select>
        )
      case "unit":
        return (
          <input
            className = "form-control text-center"
            value     = {unit}
            onChange  = {e => onChange("unit", e.target.value)}
            readOnly  = {isLabor}
          />
        )
      case "quantity":
        return (
          <input
            className = "form-control text-right"
            type      = "number"
            step      = "0.01"
            min       = "0"
            value     = {breakdown.quantity ?? ""}
            onChange  = {e => onChange("quantity", e.target.value)}
            name      = {breakdownNameFor("quantity", lineIndex, breakdownIndex)}
            title     = {qty > 0 ? undefined : I18n.t("quotes.builder.component.zero_quantity_hint")}
          />
        )
      case "quantity_total":
        return <input className="form-control text-right" value={formatNumber(totalQty)} disabled readOnly />
      case "purchase_price":
        return (
          <input
            className = "form-control text-right"
            type      = "number"
            step      = "0.01"
            value     = {breakdown.purchase_price || ""}
            onChange  = {e => onChange("purchase_price", e.target.value)}
            name      = {breakdownNameFor("purchase_price", lineIndex, breakdownIndex)}
          />
        )
      case "revient":
        return (
          <input
            className = "form-control text-right"
            type      = "text"
            value     = {revient > 0 ? formatNumber(revient) : ""}
            disabled
            readOnly
            title     = {I18n.t("quotes.new.service_cost_price_tooltip")}
          />
        )
      case "coefficient":
        return (
          <input
            className = "form-control text-right"
            type      = "text"
            inputMode = "decimal"
            disabled  = {pa <= 0}
            value     = {pa <= 0 ? "" : (coefficientDraft != null ? coefficientDraft : (coef ? roundNumber(coef) : ""))}
            onChange  = {e => { setCoefficientDraft(e.target.value); onChange("coefficient", e.target.value) }}
            onBlur    = {() => setCoefficientDraft(null)}
          />
        )
      case "unit_price":
        return (
          <input
            className = "form-control text-right"
            type      = "number"
            step      = "0.01"
            value     = {breakdown.unit_price || ""}
            onChange  = {e => onChange("unit_price", e.target.value)}
            name      = {breakdownNameFor("unit_price", lineIndex, breakdownIndex)}
            required
          />
        )
      default:
        return null
    }
  }

  const productLabel = breakdown.product_name || breakdown.ref_product_distrib
  const marginPct    = pv > 0 ? ((pv - pa) / pv) * 100 : 0

  return (
    <React.Fragment>
      <div
        className = "service-breakdown"
        style     = {{ position: "relative", width: "100%", display: "flex", flexWrap: "wrap", rowGap: "8px", marginBottom: "16px" }}
      >
        <FontAwesomeIcon
          icon      = "minus-circle"
          className = "default"
          style     = {{ position: "absolute", left: `${indent - 26}px`, top: "10px", color: "#ccc", cursor: "pointer", fontSize: "18px" }}
          onClick   = {() => onRemove(breakdownIndex)}
          title     = {I18n.t("quotes.new.service_remove_breakdown")}
        />

        <div style={{ ...breakdownLeftColumn(indent), display: "flex", gap: "8px", alignItems: "center" }}>
          <input
            className   = "form-control"
            style       = {{ flex: 1 }}
            value       = {breakdown.description || ""}
            placeholder = {categoryLabel}
            onChange    = {e => onChange("description", e.target.value)}
            name        = {breakdownNameFor("description", lineIndex, breakdownIndex)}
          />
          <div style={{ width: "260px", flex: "0 0 auto" }}>
            { breakdown.product_id
              ? <div style={{ display: "flex", alignItems: "center", gap: "6px", height: "41px", padding: "0 10px", border: "1px solid #6ee7b7", background: "#ecfdf5", borderRadius: "6px", color: "#065f46", fontSize: "13px" }}>
                  <FontAwesomeIcon icon="box" />
                  <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={productLabel}>
                    {productLabel || I18n.t("quotes.new.service_linked_article")}
                  </span>
                  <FontAwesomeIcon
                    icon    = "times-circle"
                    style   = {{ cursor: "pointer" }}
                    onClick = {() => onUnlinkProduct(breakdownIndex)}
                    title   = {I18n.t("quotes.new.service_unlink_article")}
                  />
                </div>
              : <Combobox
                  identifier      = "id"
                  feed            = "/products/autocomplete.json"
                  onSelectElement = {prod => onSelectProduct(breakdownIndex, prod.id)}
                  prompt          = {I18n.t("quotes.new.service_link_article")}
                />
            }
          </div>
        </div>

        <div style={{ width: BREAKDOWN_RIGHT_WIDTH, paddingLeft: "20px", display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "13px" }}>
          <span style={{ color: "#6b7280" }}>{I18n.t("quotes.new.service_total")}</span>
          <span style={{ fontWeight: 600 }}>{formatNumber(totalPv)} {currencySymbol || ""}</span>
        </div>

        <div style={{ ...breakdownLeftColumn(indent), display: "grid", gridTemplateColumns: breakdownGridTemplate(keys), columnGap: "5px", alignItems: "center" }}>
          { keys.map(key =>
            <div key={key} style={{ textAlign: breakdownColumnAlign(key) }}>{cell(key)}</div>
          )}
        </div>

        <div style={{ width: BREAKDOWN_RIGHT_WIDTH, paddingLeft: "20px", display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "13px" }}>
          { showMargin &&
            <React.Fragment>
              <span style={{ color: "#6b7280" }}>{I18n.t("quotes.bigcard.margin")}</span>
              <span>{formatNumber(marginPct)} %</span>
            </React.Fragment>
          }
        </div>
      </div>

      <input type="hidden" name={breakdownNameFor("id",         lineIndex, breakdownIndex)} value={breakdown.id || ""} />
      <input type="hidden" name={breakdownNameFor("order",      lineIndex, breakdownIndex)} value={breakdownIndex + 1} />
      <input type="hidden" name={breakdownNameFor("unit",       lineIndex, breakdownIndex)} value={unit} />
      <input type="hidden" name={breakdownNameFor("product_id", lineIndex, breakdownIndex)} value={breakdown.product_id || ""} />
      <input type="hidden" name={breakdownNameFor("ref_product_distrib", lineIndex, breakdownIndex)} value={breakdown.ref_product_distrib || ""} />
      <BreakdownTreeInputs breakdown={breakdown} lineIndex={lineIndex} breakdownIndex={breakdownIndex} />
    </React.Fragment>
  )
}

const SubServiceRow = ({ breakdown, lineIndex, breakdownIndex, lineQuantity, indent = 0, currencySymbol, showMargin, showCostCategory, onChange, onRemove }) => {
  const qty       = parseNum(breakdown.quantity)
  const pa        = parseNum(breakdown.purchase_price)
  const pv        = parseNum(breakdown.unit_price)
  const totalPv   = roundNumber(qty * pv * lineQuantity)
  const marginPct = pv > 0 ? ((pv - pa) / pv) * 100 : 0
  const keys      = breakdownColumnKeys(showMargin, showCostCategory)
  const readOnly  = value => <input className="form-control text-right" value={value} disabled readOnly />

  const cell = key => {
    switch (key) {
      case "category":
        return (
          <div className="form-control h-41px" style={{ display: "flex", alignItems: "center", gap: "6px", background: "#f9fafb", color: "#374151" }}>
            <FontAwesomeIcon icon="cubes" />
            {I18n.t("quotes.new.service")}
          </div>
        )
      case "unit":
        return (
          <input
            className = "form-control text-center"
            value     = {breakdown.unit || ""}
            onChange  = {e => onChange("unit", e.target.value)}
            name      = {breakdownNameFor("unit", lineIndex, breakdownIndex)}
          />
        )
      case "quantity":
        return (
          <input
            className = "form-control text-right"
            type      = "number"
            step      = "0.01"
            min       = "0"
            value     = {breakdown.quantity ?? ""}
            onChange  = {e => onChange("quantity", e.target.value)}
            name      = {breakdownNameFor("quantity", lineIndex, breakdownIndex)}
          />
        )
      case "quantity_total":
        return readOnly(formatNumber(roundNumber(qty * lineQuantity)))
      case "purchase_price":
        return readOnly(formatNumber(pa))
      case "coefficient":
        return readOnly(pa > 0 ? formatNumber(pv / pa) : "")
      case "unit_price":
        return readOnly(formatNumber(pv))
      default:
        return readOnly("")
    }
  }

  return (
    <React.Fragment>
      <div
        className = "service-breakdown service-sub-service"
        style     = {{ position: "relative", width: "100%", display: "flex", flexWrap: "wrap", rowGap: "8px", marginBottom: "8px" }}
      >
        <FontAwesomeIcon
          icon      = "minus-circle"
          className = "default"
          style     = {{ position: "absolute", left: `${indent - 26}px`, top: "10px", color: "#ccc", cursor: "pointer", fontSize: "18px" }}
          onClick   = {() => onRemove(breakdownIndex)}
          title     = {I18n.t("quotes.new.service_remove_sub_service")}
        />

        <div style={{ ...breakdownLeftColumn(indent), display: "flex", gap: "8px", alignItems: "center" }}>
          <input
            className   = "form-control"
            style       = {{ flex: 1, fontWeight: 600 }}
            value       = {breakdown.description || ""}
            placeholder = {I18n.t("quotes.new.service")}
            onChange    = {e => onChange("description", e.target.value)}
            name        = {breakdownNameFor("description", lineIndex, breakdownIndex)}
          />
          <input
            className   = "form-control"
            style       = {{ width: "260px", flex: "0 0 auto" }}
            value       = {breakdown.ref_product_distrib || ""}
            placeholder = {I18n.t("quotes.bigcard.reference")}
            onChange    = {e => onChange("ref_product_distrib", e.target.value)}
            name        = {breakdownNameFor("ref_product_distrib", lineIndex, breakdownIndex)}
          />
        </div>

        <div style={{ width: BREAKDOWN_RIGHT_WIDTH, paddingLeft: "20px", display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "13px" }}>
          <span style={{ color: "#6b7280" }}>{I18n.t("quotes.new.service_total")}</span>
          <span style={{ fontWeight: 600 }}>{formatNumber(totalPv)} {currencySymbol || ""}</span>
        </div>

        <div style={{ ...breakdownLeftColumn(indent), display: "grid", gridTemplateColumns: breakdownGridTemplate(keys), columnGap: "5px", alignItems: "center" }}>
          { keys.map(key =>
            <div key={key} style={{ textAlign: breakdownColumnAlign(key) }}>{cell(key)}</div>
          )}
        </div>

        <div style={{ width: BREAKDOWN_RIGHT_WIDTH, paddingLeft: "20px", display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "13px" }}>
          { showMargin &&
            <React.Fragment>
              <span style={{ color: "#6b7280" }}>{I18n.t("quotes.bigcard.margin")}</span>
              <span>{formatNumber(marginPct)} %</span>
            </React.Fragment>
          }
        </div>
      </div>

      <input type="hidden" name={breakdownNameFor("id",             lineIndex, breakdownIndex)} value={breakdown.id || ""} />
      <input type="hidden" name={breakdownNameFor("order",          lineIndex, breakdownIndex)} value={breakdownIndex + 1} />
      <input type="hidden" name={breakdownNameFor("cost_category",  lineIndex, breakdownIndex)} value={breakdown.cost_category || "other"} />
      <input type="hidden" name={breakdownNameFor("unit_price",     lineIndex, breakdownIndex)} value={pv} />
      <input type="hidden" name={breakdownNameFor("purchase_price", lineIndex, breakdownIndex)} value={pa} />
      <input type="hidden" name={breakdownNameFor("product_id",     lineIndex, breakdownIndex)} value="" />
      <BreakdownTreeInputs breakdown={breakdown} lineIndex={lineIndex} breakdownIndex={breakdownIndex} />
    </React.Fragment>
  )
}

export default class Service extends LineBase {
  constructor(props) {
    super(props)
    this.state = { subServicePickerKey: 0 }
    this.replaceBreakdown    = this.replaceBreakdown.bind(this)
    this.patchBreakdown      = this.patchBreakdown.bind(this)
    this.linkBreakdownProduct   = this.linkBreakdownProduct.bind(this)
    this.unlinkBreakdownProduct = this.unlinkBreakdownProduct.bind(this)
    this.addBreakdown        = this.addBreakdown.bind(this)
    this.removeBreakdown     = this.removeBreakdown.bind(this)
    this.onChangeQline       = this.onChangeQline.bind(this)
    this.changeVat           = this.changeVat.bind(this)
    this.onSelectInitOption  = this.onSelectInitOption.bind(this)
    this.insertSubService    = this.insertSubService.bind(this)
  }

  componentDidMount() {
    const { qline, qline_service_blueprints } = this.props
    if (qline._service_init_mode === SERVICE_INIT_MODE.CHOOSE && !(qline_service_blueprints && qline_service_blueprints.length)) {
      this.onSelectInitOption({ id: MANUAL_OPTION_ID })
    }
  }

  saveBreakdowns(breakdowns, patch = {}) {
    const { qline, index }  = this.props
    const { replaceLine }   = this.props.buttonProps

    const rolled = rollUpBreakdowns(breakdowns)
    const sums   = breakdownTotals(rolled)
    replaceLine({
      ...qline,
      ...patch,
      qline_breakdowns: rolled,
      purchase_price:   roundNumber(sums.purchase_price),
      unit_price:       roundNumber(sums.unit_price)
    }, index)
  }

  fetchBlueprint(id) {
    return getRequest(`/quotes/form/${id}/qline_service_blueprint.json`)
  }

  addBreakdown(costCategory) {
    const { qline } = this.props

    this.saveBreakdowns([
      ...(qline.qline_breakdowns || []),
      {
        cost_category:  costCategory,
        quantity:       1,
        unit:           costCategory === "labor" ? "h" : "",
        unit_price:     "",
        purchase_price: "",
        order:          (qline.qline_breakdowns || []).length + 1
      }
    ])
  }

  removeBreakdown(breakdownIndex) {
    const breakdowns = this.props.qline.qline_breakdowns || []
    const tree       = breakdownTree(breakdowns)
    const doomed     = new Set()
    const collect    = breakdown => {
      if (doomed.has(breakdown)) return
      doomed.add(breakdown)
      tree.childrenOf(breakdown).forEach(collect)
    }
    collect(breakdowns[breakdownIndex])

    this.saveBreakdowns(breakdowns.reduce((kept, qline_breakdown) => {
      if (!doomed.has(qline_breakdown)) {
        kept.push(qline_breakdown)
      } else if (qline_breakdown.id) {
        kept.push({ ...qline_breakdown, _destroy: true })
      }
      return kept
    }, []))
  }

  patchBreakdown(breakdownIndex, patch) {
    const { qline } = this.props

    this.saveBreakdowns((qline.qline_breakdowns || []).map((qline_breakdown, position) =>
      position === breakdownIndex ? { ...qline_breakdown, ...patch } : qline_breakdown
    ))
  }

  linkBreakdownProduct(breakdownIndex, productId) {
    const path   = `/quotes/form/${productId}/product`
    const params = { line_type: "Qline" }
    if (this.props.quote.client?.id) params.client = this.props.quote.client.id

    getRequest(pathname({ path, format: "json", params })).then(product => {
      this.patchBreakdown(breakdownIndex, {
        product_id:          product.id,
        product_name:        product.name,
        description:         product.name || "",
        ref_product_distrib: product.ref_product_brand || "",
        unit:                product.unit || "",
        purchase_price:      product.purchase_price,
        unit_price:          product.sale_price,
        ...(product.cost_category ? { cost_category: product.cost_category } : {})
      })
    })
  }

  unlinkBreakdownProduct(breakdownIndex) {
    this.patchBreakdown(breakdownIndex, { product_id: null, product_name: "" })
  }

  replaceBreakdown(breakdownIndex, attribute, value) {
    const { qline, showMargin } = this.props
    const { showCostCategory, overheadCoefs, overheadCoef, saleCoefOverrides } = this.props.buttonProps
    // When the cost-breakdown preference is on, the Coef. vente is bound to the
    // Coût de revient (PA × (1 + Coef. FG)). Without the preference we fall
    // back to the historical Coef. vente = PV / PA binding.
    const useRevient = !!showCostCategory && !!showMargin

    const breakdowns = (qline.qline_breakdowns || []).map((qline_breakdown, position) => {
      if (position !== breakdownIndex) return qline_breakdown

      const category       = qline_breakdown.cost_category || "materials"
      const coefForCat     = (overheadCoefs && overheadCoefs[category] != null)
                               ? parseNum(overheadCoefs[category])
                               : parseNum(overheadCoef) || 0
      const overheadMult   = 1 + coefForCat
      const currentPa      = parseNum(qline_breakdown.purchase_price)
      const currentPv      = parseNum(qline_breakdown.unit_price)
      const currentBase    = useRevient ? currentPa * overheadMult : currentPa
      const currentCoef    = currentBase > 0 ? currentPv / currentBase : 0

      if (attribute === "coefficient") {
        const newCoef = parseNum(value)
        if (currentPa <= 0) return qline_breakdown
        return { ...qline_breakdown, unit_price: roundNumber(currentBase * newCoef) }
      }

      if (attribute === "purchase_price" && currentCoef > 0) {
        const newPa   = parseNum(value)
        const newBase = useRevient ? newPa * overheadMult : newPa
        return { ...qline_breakdown, purchase_price: value, unit_price: roundNumber(newBase * currentCoef) }
      }

      if (attribute === "unit_price" && currentCoef > 0) {
        const newPv   = parseNum(value)
        const newBase = newPv / currentCoef
        const newPa   = useRevient ? newBase / overheadMult : newBase
        return { ...qline_breakdown, unit_price: value, purchase_price: roundNumber(newPa) }
      }

      if (attribute === "cost_category") {
        // On category change, we automatically apply the
        // coefs applied to the quote for this category:
        //   PV = PA × (1 + overhead coef_cat) × sale coef_cat
        // Overhead coef: quote override (already merged into overheadCoefs) otherwise
        //           account coef (overheadCoef). Sale coef: we
        //           ONLY use the quote override; absent that we
        //           keep the current PV (the user will enter it).
        const newCoefFG   = (overheadCoefs && overheadCoefs[value] != null)
                              ? parseNum(overheadCoefs[value])
                              : parseNum(overheadCoef) || 0
        const venteOver   = saleCoefOverrides && saleCoefOverrides[value]
        const patch       = { cost_category: value }
        if (value === "labor") patch.unit = "h"
        if (currentPa > 0 && venteOver != null && venteOver !== "") {
          const vente = parseNum(venteOver)
          if (vente > 0) {
            patch.unit_price = roundNumber(currentPa * (1 + newCoefFG) * vente)
          }
        }
        return { ...qline_breakdown, ...patch }
      }

      return { ...qline_breakdown, [attribute]: value }
    })

    this.saveBreakdowns(breakdowns)
  }

  // Only the prestation opening the line describes it, and never over a description already there.
  onSelectInitOption(option) {
    if (option.id === MANUAL_OPTION_ID) {
      this.saveBreakdowns(DEFAULT_BREAKDOWNS(), { _service_init_mode: null })
      return
    }

    this.insertSubService(option, payload => ({
      _service_init_mode: null,
      ...(isBlankHtml(this.props.qline.description) ? { description: payload.description || "" } : {})
    }))
  }

  insertSubService(option, patchFor = () => ({})) {
    if (!option?.id) return

    this.fetchBlueprint(option.id).then(payload => {
      const parent = {
        temp_id:             newBreakdownTempId(),
        cost_category:       "other",
        description:         payload.name || "",
        ref_product_distrib: payload.ref_product_distrib || "",
        unit:                payload.unit || "",
        quantity:            1,
        unit_price:          payload.unit_price     || "",
        purchase_price:      payload.purchase_price || ""
      }
      this.saveBreakdowns([
        ...(this.props.qline.qline_breakdowns || []),
        parent,
        ...breakdownsFromBlueprint(payload, parent.temp_id)
      ], patchFor(payload))
      this.setState(state => ({ subServicePickerKey: state.subServicePickerKey + 1 }))
    })
  }

  onChangeQline(e, attribute) {
    e.preventDefault()
    const { qline, index } = this.props
    const { replaceLine }  = this.props.buttonProps
    replaceLine({ ...qline, [attribute]: e.target.value }, index)
  }

  changeVat(e) {
    e.preventDefault()
    const { qline, index, vats } = this.props
    const { replaceLine } = this.props.buttonProps
    const vat = vats.find(v => v.id == e.target.value)
    replaceLine({ ...qline, vat_rate: vat || {} }, index)
  }

  renderInitSelector() {
    const { index, qline, qline_service_blueprints, buttonProps } = this.props

    return (
      <div className="quotes--form-qline-container service-line">
        <div className="text-input">
          <Combobox
            options         = {initOptions(qline_service_blueprints)}
            value           = {null}
            onSelectElement = {this.onSelectInitOption}
            prompt          = {I18n.t("quotes.new.service_select_blueprint")}
            required        = {true}
          />
        </div>

        <Buttons
          index       = {index}
          qline       = {qline}
          buttonProps = {buttonProps}
        />
      </div>
    )
  }

  renderBreakdowns(breakdowns, lineQuantity) {
    const { index, qline, showMargin } = this.props
    const { showCostCategory, overheadCoefs, overheadCoef } = this.props.buttonProps
    const enabledCategoriesBase = enabledCategoriesFor(this.props.quote.account)

    const tree      = breakdownTree(breakdowns)
    const positions = new Map(breakdowns.map((breakdown, position) => [breakdown, position]))

    const renderNodes = (nodes, multiplier, depth) => nodes.map(breakdown => {
      const breakdownIndex = positions.get(breakdown)
      const children       = tree.childrenOf(breakdown)
      const indent         = depth * BREAKDOWN_INDENT

      if (children.length > 0) {
        return (
          <React.Fragment key={breakdownIndex}>
            <SubServiceRow
              breakdown        = {breakdown}
              lineIndex        = {index}
              breakdownIndex   = {breakdownIndex}
              lineQuantity     = {multiplier}
              indent           = {indent}
              currencySymbol   = {qline.currency_symbol}
              showMargin       = {showMargin}
              showCostCategory = {showCostCategory}
              onChange         = {(attr, value) => this.patchBreakdown(breakdownIndex, { [attr]: value })}
              onRemove         = {this.removeBreakdown}
            />
            <div style={{ position: "relative" }}>
              <div style={{ position: "absolute", left: `${indent + 2}px`, top: 0, bottom: "16px", borderLeft: "2px solid #e5e7eb" }} />
              {renderNodes(children, multiplier * parseNum(breakdown.quantity), depth + 1)}
            </div>
          </React.Fragment>
        )
      }

      return (
        <BreakdownRow
          key              = {breakdownIndex}
          breakdown        = {breakdown}
          lineIndex        = {index}
          breakdownIndex   = {breakdownIndex}
          lineQuantity     = {multiplier}
          indent           = {indent}
          currencySymbol   = {qline.currency_symbol}
          showMargin       = {showMargin}
          showCostCategory = {showCostCategory}
          overheadCoefs    = {overheadCoefs}
          overheadCoef     = {overheadCoef}
          enabledCategories = {enabledCategoriesBase.includes(breakdown.cost_category) || !breakdown.cost_category
                                 ? enabledCategoriesBase
                                 : [...enabledCategoriesBase, breakdown.cost_category]}
          onChange         = {(attr, value) => this.replaceBreakdown(breakdownIndex, attr, value)}
          onRemove         = {this.removeBreakdown}
          onSelectProduct  = {this.linkBreakdownProduct}
          onUnlinkProduct  = {this.unlinkBreakdownProduct}
        />
      )
    })

    return renderNodes(tree.roots, lineQuantity, 0)
  }

  render() {
    const { index, quote, qline, vats, showCode, showMargin, buttonProps } = this.props
    const { replaceLine, showCostCategory } = this.props.buttonProps
    const qline_service_blueprints = this.props.qline_service_blueprints || []
    const breakdowns      = qline.qline_breakdowns || []
    const enabledCategoriesBase = enabledCategoriesFor(quote.account)

    if (!qline.active) {
      return (
        <Inactive
          index       = {index}
          qline       = {qline}
          showCode    = {showCode}
          buttonProps = {buttonProps}
        />
      )
    }

    if (qline._service_init_mode === SERVICE_INIT_MODE.CHOOSE && qline_service_blueprints.length > 0) {
      return this.renderInitSelector()
    }

    const sectionLabelStyle = {
      fontSize: '0.85em',
      color: '#374151',
      fontWeight: 600,
      marginBottom: '4px',
      marginTop: '8px'
    }

    const sums                 = breakdownTotals(breakdowns)
    const computedSaleUnit     = sums.unit_price
    const computedPurchaseUnit = sums.purchase_price

    const lineQ             = parseNum(qline.quantity) || 1
    const totalLinePurchase = roundNumber(computedPurchaseUnit * lineQ)
    const totalLineSale     = roundNumber(computedSaleUnit     * lineQ)
    const sharedGridCols    = `repeat(${qlineColCount(showMargin)}, 1fr)`
    const currency          = qline.currency_symbol || ""

    return (
      <div className="quotes--form-qline-container service-line">
        <div className="text-input">
          <QuillInput
            text     = {qline.description}
            name     = {lineNameFor('description', index)}
            onChange = {content => replaceLine({ ...qline, description: content }, index)}
          />
        </div>

        <Calculations
          qline       = {qline}
          index       = {index}
          replaceLine = {replaceLine}
          showMargin  = {showMargin}
          showPriceCoefficient = {false}
        />

        <div className="row-inputs service-line-row" style={{ gridTemplateColumns: sharedGridCols, marginBottom: '8px' }}>
            <div>
              <label>{I18n.t("quotes.bigcard.reference")}</label>
              <input
                className = "form-control"
                value     = {qline.ref_product_distrib || ""}
                onChange  = {e => this.onChangeQline(e, "ref_product_distrib")}
                name      = {lineNameFor("ref_product_distrib", index)}
              />
            </div>

            <div>
              <label required>{I18n.t("quotes.new.quantity")}</label>
              <input
                className = "form-control text-right"
                type      = "number"
                step      = "0.01"
                value     = {qline.quantity || ""}
                onChange  = {e => this.onChangeQline(e, "quantity")}
                name      = {lineNameFor("quantity", index)}
                required
              />
            </div>

            <div>
              <label>{I18n.t("quotes.bigcard.unit")}</label>
              <input
                className = "form-control"
                value     = {qline.unit || ""}
                onChange  = {e => this.onChangeQline(e, "unit")}
                name      = {lineNameFor("unit", index)}
              />
            </div>

            { showMargin &&
              <div>
                <label>{I18n.t("quotes.new.purchase_price")}</label>
                <input
                  className = "form-control text-right"
                  value     = {formatNumber(totalLinePurchase)}
                  disabled
                  readOnly
                />
                <input type="hidden" name={lineNameFor("purchase_price", index)} value={computedPurchaseUnit} />
              </div>
            }

            <div>
              <label>{I18n.t("quotes.new.sale_price")}</label>
              <input
                className = "form-control text-right"
                value     = {formatNumber(totalLineSale)}
                disabled
                readOnly
              />
              <input type="hidden" name={lineNameFor("unit_price", index)} value={computedSaleUnit} />
            </div>

            <div>
              <label required>{I18n.t("quotes.bigcard.vat")}</label>
              <select
                className = "form-control height-41"
                value     = {qline.vat_rate.id || ""}
                onChange  = {this.changeVat}
                name      = {lineNameFor("vat_rate_id", index)}
                disabled  = {quote.read_only_vat}
                required
              >
                <option value="">{I18n.t("sites.select_vat")}</option>
                { vats.map((vat, vatIndex) =>
                  <option key={vatIndex} value={vat.id}>{vat.label}</option>
                )}
              </select>
            </div>

            <div>
              <label>{I18n.t("quotes.new.discount")}</label>
              <input
                className = "form-control text-right"
                type      = "number"
                step      = "0.01"
                max       = "100"
                value     = {qline.discount || ""}
                onChange  = {e => this.onChangeQline(e, "discount")}
                name      = {lineNameFor("discount", index)}
              />
            </div>
          </div>

        <div className="checkboxes">
          <Option
            index       = {index}
            qline       = {qline}
            replaceLine = {replaceLine}
          />

          <HideInPdf
            index       = {index}
            qline       = {qline}
            replaceLine = {replaceLine}
          />
        </div>

        { breakdowns.some(breakdown => !breakdown._destroy) &&
          <BreakdownHeader showMargin={showMargin} showCostCategory={showCostCategory} />
        }

        { breakdowns.map((breakdown, breakdownIndex) =>
          breakdown._destroy && breakdown.id &&
            <React.Fragment key={breakdownIndex}>
              <input type="hidden" name={breakdownNameFor("id",       index, breakdownIndex)} value={breakdown.id} />
              <input type="hidden" name={breakdownNameFor("_destroy", index, breakdownIndex)} value="1" />
            </React.Fragment>
        )}

        { this.renderBreakdowns(breakdowns, lineQ) }

        <div className="service-add-breakdowns" style={{ marginTop: '8px', marginBottom: '12px' }}>
          <div style={sectionLabelStyle}>{I18n.t("quotes.new.service_add_cost")}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            { enabledCategoriesBase.map(category =>
              <button
                key       = {category}
                type      = "button"
                onClick   = {() => this.addBreakdown(category)}
                style     = {{
                  display:      'inline-flex',
                  alignItems:   'center',
                  gap:          '6px',
                  padding:      '6px 12px',
                  borderRadius: '6px',
                  cursor:       'pointer',
                  border:       `1px solid ${CATEGORY_COLORS[category] || '#d1d5db'}`,
                  background:   '#fff',
                  color:        CATEGORY_COLORS[category] || '#374151',
                  fontSize:     '13px',
                  fontWeight:   500
                }}
              >
                <FontAwesomeIcon icon="plus-circle" />
                {I18n.t(`quotes.new.cost_category.${category}`)}
              </button>
            )}
          </div>

          { qline_service_blueprints.length > 0 &&
            <React.Fragment>
              <div style={sectionLabelStyle}>{I18n.t("quotes.new.service_add_sub_service")}</div>
              <div style={{ maxWidth: "420px" }}>
                <Combobox
                  key             = {this.state.subServicePickerKey}
                  options         = {qline_service_blueprints}
                  value           = {null}
                  onSelectElement = {this.insertSubService}
                  prompt          = {I18n.t("quotes.new.service_select_blueprint")}
                />
              </div>
            </React.Fragment>
          }
        </div>

        <Buttons
          index       = {index}
          qline       = {qline}
          buttonProps = {buttonProps}
        />

        <input type="hidden" name={lineNameFor("id",    index)} defaultValue={qline.id} />
        <input type="hidden" name={lineNameFor("order", index)} defaultValue={index + 1} />
      </div>
    )
  }
}
